/**
 * Compact offline indicator with outbox badge
 * Shows when device is offline and displays pending operations count
 */

import { useEffect, useState } from 'react';
import { WifiOff, CloudOff } from 'lucide-react';
import { useOnlineStatus } from '@/hooks/useOnlineStatus';
import { getOutboxCount } from '@/lib/services/offlineOutboxService';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { cn } from '@/lib/utils';

interface OfflineIndicatorProps {
  className?: string;
  showWhenOnline?: boolean; // Show indicator when online but has pending operations
  size?: 'sm' | 'md';
}

export function OfflineIndicator({ 
  className, 
  showWhenOnline = true,
  size = 'md' 
}: OfflineIndicatorProps) {
  const { isOnline } = useOnlineStatus();
  const [outboxCount, setOutboxCount] = useState(0);

  useEffect(() => {
    // Initial count
    setOutboxCount(getOutboxCount());

    // Listen for outbox updates
    const handleOutboxUpdate = (event: CustomEvent<{ count: number }>) => {
      setOutboxCount(event.detail.count);
    };

    window.addEventListener('outbox-updated', handleOutboxUpdate as EventListener);

    return () => {
      window.removeEventListener('outbox-updated', handleOutboxUpdate as EventListener);
    };
  }, []);

  // Don't show if online and no pending operations (unless showWhenOnline is false)
  if (isOnline && outboxCount === 0) {
    return null;
  }

  // If online but has pending operations
  if (isOnline && outboxCount > 0 && !showWhenOnline) {
    return null;
  }

  const iconSize = size === 'sm' ? 'w-4 h-4' : 'w-5 h-5';
  const badgeSize = size === 'sm' ? 'w-3.5 h-3.5 text-[10px]' : 'w-4 h-4 text-xs';

  const tooltipText = isOnline 
    ? `${outboxCount} операций ожидают синхронизации`
    : outboxCount > 0 
      ? `Нет соединения. ${outboxCount} операций сохранено локально`
      : 'Нет соединения';

  const Icon = isOnline ? CloudOff : WifiOff;

  return (
    <TooltipProvider>
      <Tooltip>
        <TooltipTrigger asChild>
          <div className={cn('relative inline-flex items-center justify-center', className)}>
            <Icon 
              className={cn(
                iconSize,
                isOnline ? 'text-amber-500' : 'text-destructive'
              )} 
            />
            {outboxCount > 0 && (
              <span 
                className={cn(
                  'absolute -top-1 -right-1 flex items-center justify-center rounded-full font-medium',
                  badgeSize,
                  isOnline ? 'bg-amber-500 text-white' : 'bg-destructive text-destructive-foreground'
                )}
              >
                {outboxCount > 9 ? '9+' : outboxCount}
              </span>
            )}
          </div>
        </TooltipTrigger>
        <TooltipContent side="bottom">
          <p className="text-sm">{tooltipText}</p>
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}
